import i18n from '../i18n';

/**
 * Utility for getting translated error messages in the service layer
 * This allows us to use i18n in services/API clients without React hooks
 */

/**
 * Get a translated error message
 * @param key - Translation key (e.g., 'errors.networkError')
 * @param defaultMessage - Fallback message if translation is not found
 * @param options - Optional interpolation options
 */
export const getErrorMessage = (
  key: string,
  defaultMessage?: string,
  options?: Record<string, any>
): string => {
  try {
    const translated = i18n.t(key, options);
    // Ensure we always return a string (i18n.t can return string | object)
    const translatedStr = typeof translated === 'string' ? translated : String(translated);
    // If translation returns the key itself, use default message
    return translatedStr !== key ? translatedStr : (defaultMessage || translatedStr);
  } catch (error) {
    console.warn(`Translation error for key: ${key}`, error);
    return defaultMessage || key;
  }
};

/**
 * Predefined error message getters for common API errors
 */
export const ErrorMessages = {
  // Network and connection errors
  networkError: () => getErrorMessage('errors.networkError', 'Network error. Please try again.'),
  serverError: () => getErrorMessage('errors.serverError', 'Server error. Please contact support.'),
  
  // Authentication errors
  sessionExpired: () => getErrorMessage('errors.sessionExpired', 'Your session has expired. Please log in again.'),
  unauthorized: () => getErrorMessage('errors.unauthorized', 'Unauthorized access'),
  forbidden: () => getErrorMessage('errors.forbidden', 'You do not have permission to access this resource.'),
  invalidCredentials: () => getErrorMessage('errors.invalidCredentials', 'Invalid credentials. Please check your phone number and password.'),
  
  // Resource errors
  notFound: () => getErrorMessage('errors.notFound', 'Item not found'),
  
  // Validation errors
  validationError: () => getErrorMessage('errors.validationError', 'Validation error'),
  
  // CRUD operation errors
  deleteError: () => getErrorMessage('errors.deleteError', 'Failed to delete item'),
  updateError: () => getErrorMessage('errors.updateError', 'Failed to update item'),
  createError: () => getErrorMessage('errors.createError', 'Failed to create item'),
  
  // Rate limiting
  rateLimitExceeded: (retryAfter?: number) => 
    getErrorMessage(
      'errors.rateLimitExceeded',
      `Too many requests. Please try again in ${retryAfter || 60} seconds.`,
      { retryAfter }
    ),
  
  // Generic error
  unexpectedError: () => getErrorMessage('errors.unexpectedError', 'An unexpected error occurred.'),
};

/**
 * Get error message by HTTP status code
 */
export const getErrorMessageByStatus = (statusCode: number, customMessage?: string): string => {
  switch (statusCode) {
    case 400:
      return customMessage || ErrorMessages.validationError();
    case 401:
      return customMessage || ErrorMessages.sessionExpired();
    case 403:
      return customMessage || ErrorMessages.forbidden();
    case 404:
      return customMessage || ErrorMessages.notFound();
    case 429:
      return customMessage || ErrorMessages.rateLimitExceeded();
    case 500:
    case 502:
    case 503:
    case 504:
      return customMessage || ErrorMessages.serverError();
    default:
      return customMessage || ErrorMessages.unexpectedError();
  }
};

/**
 * Current UI language code for formatters (e.g. `ar`, `en`).
 */
export const getLocale = (): string => i18n.language || "ar";

export default {
  getErrorMessage,
  ErrorMessages,
  getErrorMessageByStatus,
};
